/**
 * TipToast (#1717/#1718, epic #1589) - rotating feature-discovery toast.
 *
 * Mounted ONCE in AppContent (outside <Routes>), so an open toast survives
 * page navigation for its full display window. Docked above the fixed bottom
 * nav, right-aligned, zIndex below the nav (1000), its hover drawer (999),
 * and the error-toast stacks (1400+) so none of those are ever covered.
 *
 * Behavior:
 * - fetches GET /api/tips once the session user is ready (copy arrives
 *   pre-localized; the client never translates)
 * - shows a random eligible tip when localStorage's last-shown stamp is
 *   absent or older than intervalMinutes; re-checks every minute while the
 *   tab is visible
 * - auto-hides after displaySeconds and stamps the clock
 * - x hides for the rest of the session (in-memory)
 * - "Don't show this again" writes the tipId into preferences.dismissedTips
 *   via PUT /api/profile (#1718). The write sends the FULL merged
 *   preferences object: the backend replaces preferences wholesale, so a
 *   partial write would wipe language/voiceGender.
 * - empty tip list or any fetch error renders nothing (silent, like
 *   PendingInvitesBanner)
 * - `suppressed` (#1876): while the Feature Spotlight dialog is open no tip
 *   starts, and an open one hides without stamping the clock
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IconBulb, IconX } from '@tabler/icons-react';
import { apiGlobal } from '../services/api';
import { updateProfile } from '../services/profileAPI';
import { useUserSession } from '../contexts/UserSessionContext.jsx';
import { ACTION_BAR_HEIGHT_VAR } from './ds/ActionBar';

const LAST_SHOWN_KEY = 'budgetrecon.lastTipShownAt';
const CHECK_EVERY_MS = 60 * 1000;

const readLastShown = () => {
  try {
    const v = Number(localStorage.getItem(LAST_SHOWN_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
};

const stampLastShown = () => {
  try {
    localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
  } catch {
    /* storage unavailable - toast just shows more often */
  }
};

const TipToast = ({ suppressed = false }) => {
  const session = useUserSession();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [profileVersion, setProfileVersion] = useState(null);
  const sessionHiddenRef = useRef(new Set());
  const hideTimerRef = useRef(null);

  // Seed dismissed set + optimistic-lock version from the session profile.
  useEffect(() => {
    if (!session.user) return;
    setDismissed(new Set(session.user.preferences?.dismissedTips || []));
    if (typeof session.user.version === 'number') setProfileVersion(session.user.version);
  }, [session.user]);

  // Fetch the localized tip list once the session is ready.
  useEffect(() => {
    if (session.loading || !session.user) return;
    let alive = true;
    apiGlobal.get('/tips')
      .then((res) => { if (alive) setData(res); })
      .catch((err) => { console.warn('TipToast: load failed', err); });
    return () => { alive = false; };
  }, [session.loading, session.user]);

  const hide = useCallback((stamp = true) => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (stamp) stampLastShown();
    setActive(null);
  }, []);

  const maybeShow = useCallback(() => {
    if (!data || active || suppressed) return;
    if (document.visibilityState !== 'visible') return;
    const last = readLastShown();
    const intervalMs = (data.intervalMinutes || 60) * 60 * 1000;
    if (last && Date.now() - last < intervalMs) return;
    const pool = (data.tips || []).filter(
      (t) => !dismissed.has(t.tipId) && !sessionHiddenRef.current.has(t.tipId)
    );
    if (pool.length === 0) return;
    const tip = pool[Math.floor(Math.random() * pool.length)];
    setActive(tip);
    hideTimerRef.current = setTimeout(() => {
      stampLastShown();
      setActive(null);
      hideTimerRef.current = null;
    }, (data.displaySeconds || 30) * 1000);
  }, [data, active, dismissed, suppressed]);

  // The announcement dialog takes the screen: step aside without stamping,
  // so the rotation resumes once it closes.
  useEffect(() => {
    if (suppressed && active) hide(false);
  }, [suppressed, active, hide]);

  // Immediate check when data lands, then a minute-cadence re-check gated on
  // tab visibility - roughly hourly rotation while the app stays open.
  useEffect(() => {
    maybeShow();
    const id = setInterval(maybeShow, CHECK_EVERY_MS);
    return () => clearInterval(id);
  }, [maybeShow]);

  // Unmount safety: never leak the display timer.
  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const handleClose = () => {
    if (active) sessionHiddenRef.current.add(active.tipId);
    hide();
  };

  const handleNeverAgain = () => {
    if (!active) return;
    const next = [...new Set([...dismissed, active.tipId])];
    setDismissed(new Set(next));
    if (typeof profileVersion === 'number') {
      // Full merged preferences: the backend replaces the object wholesale.
      const preferences = { ...(session.user?.preferences || {}), dismissedTips: next };
      updateProfile({ preferences, version: profileVersion })
        .then((res) => { if (res?.user?.version != null) setProfileVersion(res.user.version); })
        .catch(() => {});
    }
    hide();
  };

  const handleLink = () => {
    if (active?.linkPath) navigate(active.linkPath);
    hide();
  };

  if (!active) return null;

  return (
    <Box
      data-testid="tip-toast"
      role="status"
      sx={(t) => ({
        position: 'fixed',
        right: '16px',
        bottom: `calc(64px + var(${ACTION_BAR_HEIGHT_VAR}, 0px))`,
        zIndex: 950,
        maxWidth: '360px',
        width: 'calc(100vw - 32px)',
        backgroundColor: t.palette.surface.raised,
        border: `1px solid ${t.palette.overlay.border}`,
        borderRadius: `${t.ds.radius.overlay}px ${t.ds.radius.overlay}px 0 0`,
        boxShadow: t.palette.overlay.shadow,
        overflow: 'hidden',
      })}
    >
      <Box sx={{ display: 'flex', gap: '10px', p: '14px 12px 12px 14px' }}>
        <Box
          sx={(t) => ({
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: `${t.ds.radius.icon}px`,
            backgroundColor: t.palette.tint.interactive,
            color: t.palette.interactive,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <IconBulb size={18} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>
            {active.title}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: '2px' }}>
            {active.body}
            {active.linkPath && (
              <>
                {' '}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={handleLink}
                  sx={{ fontSize: '13px', color: 'interactive', verticalAlign: 'baseline' }}
                >
                  Take me there
                </MuiLink>
              </>
            )}
          </Typography>
          <MuiLink
            component="button"
            type="button"
            onClick={handleNeverAgain}
            sx={{ fontSize: '12px', color: 'text.secondary', mt: '8px', display: 'inline-block' }}
          >
            Don't show this again
          </MuiLink>
        </Box>
        <IconButton
          size="small"
          aria-label="close suggestion"
          onClick={handleClose}
          sx={{ alignSelf: 'flex-start', color: 'text.secondary', p: '2px' }}
        >
          <IconX size={16} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TipToast;
